// What the Create page says when a request can't go ahead or comes back refused (plan step F14;
// promptchan-backend docs/architecture/create-page.md). Each message was written out by hand at every place
// it is shown; it is now written once, word for word as it was. A Message says which modal shows it (the
// error modal or the upgrade-prompt "limit" modal) and the limit modal's title.
import Link from 'next/link'
import type { ReactNode } from 'react'

import DailyGemsCta from '../../../component/DailyGemsCta'

export type Message = {
  modal: 'error' | 'limit'
  /** The limit modal's title. The error modal has its own; the page sets this for both, as it always has. */
  title?: string
  body: ReactNode
}

type T = (key: string) => string
type Subscription = { plan?: string | null } | null | undefined

const thumbnailDesktop = '/assets/thumbnailDesktop.webp'

/**
 * A paid plan, by the backend's internal ids ('Studio' is Pro). Max needs no entry: the backend reports a Max
 * subscriber as plan 'Pro' with subPlan 'Max'. `null` while the subscription loads, so that reads as Free.
 */
export const isPaidPlan = (subscription: Subscription) =>
  !!subscription &&
  ['Studio', 'Plus', 'Premium', 'Pro'].includes(subscription.plan ?? '')

const continueTitle = (t: T) => `🚀 ${t('upgradetocontinuegenerating')}`

/**
 * Free, out of free images, before anything is sent. With the landing video above the text where the page
 * shows it (image and video generation; not the tweak).
 */
export const freeImagesUsedUp = (
  t: T,
  { video }: { video: boolean },
): Message => ({
  modal: 'limit',
  title: continueTitle(t),
  body: (
    <div className="text-center">
      {video && (
        <>
          <Link href="/upgrade" className="mb-6">
            <video autoPlay loop muted playsInline poster={thumbnailDesktop}>
              <source src="/assets/landingVideoSafe.webm" />
            </video>
          </Link>
          <br />
        </>
      )}
      <p>
        {t('youveusedallyourfreeimages')}{' '}
        <Link href="/upgrade" className="underline text-purple-300">
          {t('upgradeyouraccount')}
        </Link>{' '}
        {t('nowtounlockunlimitedimages')}
      </p>
      <DailyGemsCta />
    </div>
  ),
})

/**
 * The backend refused for want of gems (402). Free is offered an upgrade (and the daily gems where the page
 * shows them); Plus and Premium an upgrade where `upgradePlusAndPremium`; everyone else gems.
 */
export const notEnoughGems = (
  t: T,
  subscription: Subscription,
  {
    dailyGems,
    upgradePlusAndPremium,
  }: { dailyGems: boolean; upgradePlusAndPremium: boolean },
): Message => {
  if (!subscription || subscription.plan === 'Free') {
    return {
      modal: 'limit',
      title: continueTitle(t),
      body: (
        <div className="text-center">
          {t('youveusedupallyourfreeimages')}
          <br />
          <Link href="/upgrade" className="underline text-purple-300">
            {t('upgradeyouraccount')}
          </Link>{' '}
          {t('nowtounlockunlimitedimages')}
          {dailyGems && <DailyGemsCta />}
        </div>
      ),
    }
  }
  if (
    upgradePlusAndPremium &&
    (subscription.plan === 'Plus' || subscription.plan === 'Premium')
  ) {
    return {
      modal: 'limit',
      title: `🔥 ${t('upgradetocontinuegenerating')}`,
      body: (
        <div className="text-center">
          {t('outOfGems')}
          <br />
          <Link href="/upgrade" className="underline text-purple-300">
            {t('upgradeyouraccount')}
          </Link>{' '}
          {t('nowtounlockunlimitedimages')}
        </div>
      ),
    }
  }
  return {
    modal: 'limit',
    title: `🚀 ${t('outOfGems')}`,
    body: (
      <div className="text-center">
        {t('youreOutOfGems')}
        <br />
        <a href="/gems" className="underline text-purple-300">
          {t('getGemsTo')}
        </a>{' '}
        {t('nowToUnlock')}
      </div>
    ),
  }
}

/**
 * Something failed that the page can't name. A paid plan is told the servers are busy (in the error modal,
 * with `paidTitle`; `paidText` where the page says something more specific); Free is offered priority.
 */
export const overloaded = (
  t: T,
  subscription: Subscription,
  { paidTitle, paidText }: { paidTitle: string; paidText?: ReactNode },
): Message =>
  isPaidPlan(subscription)
    ? {
        modal: 'error',
        title: paidTitle,
        body: (
          <div className="text-center">
            {paidText ?? t('serversarecurrentlyoverloaded')}
          </div>
        ),
      }
    : {
        modal: 'limit',
        title: `🚀 ${t('upgradetogetpriorityaccess')}`,
        body: (
          <div className="text-center">
            {t('freeserversarecurrentlyoverloaded')}{' '}
            <Link href="/upgrade" className="underline text-white">
              {t('upgradeyouraccount')}
            </Link>{' '}
            {t('toenjoyfasterspeedsandpriorityaccess')}{' '}
          </div>
        ),
      }

/** The prompt was refused by moderation (300), naming the word when the backend does. */
export const bannedWord = (
  t: T,
  json: { banned_word?: string; error?: string } | null | undefined,
): Message => ({
  modal: 'error',
  body: (
    <div className="text-center text-white">
      {json?.banned_word ? (
        <>
          {t('thispromptcontainsbannedwords')} ("{json.banned_word}").
        </>
      ) : (
        json?.error || t('thispromptcontainsbannedwords')
      )}
    </div>
  ),
})

/**
 * The result may be NSFW and Safe Mode hides it (301). Safe countries can't turn Safe Mode off, so they get
 * the region reason instead of a link to a Settings toggle that isn't rendered.
 */
export const hiddenBySafeMode = (safeCountry: boolean): Message => ({
  modal: 'error',
  body: (
    <div className="text-center text-white">
      {safeCountry ? (
        <>
          This image may contain NSFW content and isn&apos;t available in your
          region.
        </>
      ) : (
        <>
          This image may contain NSFW content and is hidden while Safe Mode is
          on.{' '}
          <Link href="/settings" className="underline text-purple-300">
            Disable Safe Mode.
          </Link>
        </>
      )}
    </div>
  ),
})
